/**
 * Button Component
 * Reusable button with multiple variants and sizes
 * Supports rendering as different elements (button, Link, a)
 */

import React from 'react';
import './Button.css';

/**
 * @param {Object} props
 * @param {React.ElementType} [props.as='button'] - Element type to render as
 * @param {'primary'|'secondary'|'outline'|'ghost'} [props.variant='primary'] - Visual variant
 * @param {'small'|'medium'|'large'} [props.size='medium'] - Size variant
 * @param {boolean} [props.fullWidth=false] - Whether button takes full width
 * @param {boolean} [props.disabled=false] - Disabled state
 * @param {React.ReactNode} [props.icon] - Optional icon element
 * @param {React.ReactNode} props.children - Button content
 */
function Button({
    as: Component = 'button',
    variant = 'primary',
    size = 'medium',
    fullWidth = false,
    disabled = false,
    icon,
    children,
    className = '',
    ...props
}) {
    const classNames = [
        'button',
        `button--${variant}`,
        size !== 'medium' && `button--${size}`,
        fullWidth && 'button--full-width',
        className
    ].filter(Boolean).join(' ');

    return (
        <Component
            className={classNames}
            disabled={Component === 'button' ? disabled : undefined}
            {...props}
        >
            {icon && <span className="button__icon" aria-hidden="true">{icon}</span>}
            {children}
        </Component>
    );
}

export default Button;
